import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Slider,
  Button,
  Stack,
  Alert,
} from '@mui/joy';

interface AddKeywordModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: KeywordFormData) => Promise<void>;
  defaultCenterLat?: number;
  defaultCenterLng?: number;
  defaultBusinessName?: string;
}

export interface KeywordFormData {
  keyword: string;
  business_name: string;
  grid_size: number;
  grid_radius_miles: number;
  center_lat: number;
  center_lng: number;
  scan_frequency: string;
}

export const AddKeywordModal: React.FC<AddKeywordModalProps> = ({
  open,
  onClose,
  onSubmit,
  defaultCenterLat,
  defaultCenterLng,
  defaultBusinessName,
}) => {
  const [keyword, setKeyword] = useState('');
  const [businessName, setBusinessName] = useState(defaultBusinessName || '');
  const [gridSize, setGridSize] = useState<number>(7);
  const [radiusMiles, setRadiusMiles] = useState<number>(5);
  const [centerLat, setCenterLat] = useState<string>(defaultCenterLat?.toString() || '');
  const [centerLng, setCenterLng] = useState<string>(defaultCenterLng?.toString() || '');
  const [scanFrequency, setScanFrequency] = useState<string>('biweekly');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start each visit from a blank form...
  useEffect(() => {
    if (open) {
      setKeyword('');
      setBusinessName('');
      setCenterLat('');
      setCenterLng('');
      setGridSize(7);
      setRadiusMiles(5);
      setScanFrequency('biweekly');
      setError(null);
    }
  }, [open]);

  // ...then seed it. The centre and business name come from the organization
  // profile, which is fetched: opening the dialog before that query lands and
  // typing into it must not have the answer arrive and wipe the form, so this
  // only fills fields still left empty.
  useEffect(() => {
    if (!open) return;
    setBusinessName((prev) => prev || defaultBusinessName || '');
    setCenterLat((prev) => prev || defaultCenterLat?.toString() || '');
    setCenterLng((prev) => prev || defaultCenterLng?.toString() || '');
  }, [open, defaultCenterLat, defaultCenterLng, defaultBusinessName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!keyword.trim()) {
      setError('Keyword is required');
      return;
    }
    if (!centerLat || !centerLng) {
      setError('Center coordinates are required');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        keyword: keyword.trim(),
        business_name: businessName.trim(),
        grid_size: gridSize,
        grid_radius_miles: radiusMiles,
        center_lat: parseFloat(centerLat),
        center_lng: parseFloat(centerLng),
        scan_frequency: scanFrequency,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to add keyword');
    } finally {
      setSubmitting(false);
    }
  };

  const gridPoints = gridSize * gridSize;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 500 }}>
        <ModalClose />
        <Typography level="h4">Add Ranking Keyword</Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Track how your business ranks for this keyword across a geographic grid.
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Keyword</FormLabel>
              <Input
                placeholder="e.g., roofing contractor"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Business Name (for matching in results)</FormLabel>
              <Input
                placeholder="e.g., Apex Roofing & Construction LLC"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Center Latitude</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { step: 0.0001 } }}
                  value={centerLat}
                  onChange={(e) => setCenterLat(e.target.value)}
                />
              </FormControl>
              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Center Longitude</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { step: 0.0001 } }}
                  value={centerLng}
                  onChange={(e) => setCenterLng(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>
                Scan Radius: {radiusMiles} miles ({gridPoints} points)
              </FormLabel>
              <Slider
                value={radiusMiles}
                onChange={(_e, val) => setRadiusMiles(val as number)}
                min={1}
                max={15}
                step={0.5}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v} mi`}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Scan Frequency</FormLabel>
              <Select
                value={scanFrequency}
                onChange={(_e, val) => val && setScanFrequency(val)}
              >
                <Option value="biweekly">Bi-weekly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>
            </FormControl>

            <Button type="submit" loading={submitting}>
              Add Keyword
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};
